import {CommonModule} from '@angular/common';
import {ChangeDetectionStrategy, Component, EventEmitter, Input, OnDestroy, Output, signal} from '@angular/core';
import {MatIconModule} from '@angular/material/icon';
import {Router, RouterModule} from '@angular/router';
import {select, Store} from '@ngrx/store';
import {filter, map, Observable, Subject, takeUntil} from 'rxjs';

import {
  MENU_ITEMS,
  MenuItem,
  MENU_NAV_ITEMS,
  MenuNavItems,
  MenuItemGroupKey,
  MenuNavItem,
  USER_MENU_ITEMS,
  MENU_ITEMS_MAP,
} from '../../../../../app/app.constants';
import {selectActiveUserRole} from '../../../../core/store/active-user/active-user.selector';
import {NavbarItemComponent} from '../../shared/navbar-item/navbar-item.component';
import {NavbarUserComponent} from '../../shared/navbar-user/navbar-user.component';
import {Role} from '../../../../core/model/role';
import {IsRouteActivePipe} from '../../shared/pipes/is-route-active.pipe';
import {getMenuItemsByRole} from '../../../../shared/directives/utils/get-role-permission-by-key';
import {VERSION} from '../../../../app.constants';
import {RouteItemGroupComponent} from '../../shared/route-item-group/route-item-group.component';
import {MenuRoutesService} from '../../services/menu-routes.service';
import {MobileService} from '../../../../core/page/mobile.service';
import {TimezoneService} from 'src/app/services/timezone.service';

@Component({
  selector: 'desktop-sidebar',
  templateUrl: './desktop-sidebar.component.html',
  styleUrls: ['./desktop-sidebar.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    MatIconModule,
    NavbarUserComponent,
    NavbarItemComponent,
    IsRouteActivePipe,
    RouteItemGroupComponent,
  ],
})
export class DesktopSidebarComponent implements OnDestroy {
  @Input() routerUrl: string;
  @Output() logout = new EventEmitter();

  userRole$: Observable<Role> = this.store$.pipe(select(selectActiveUserRole));
  filteredMenuItems$: Observable<MenuItem[]>;
  version = VERSION;
  isMenuOpen = signal<boolean>(false);
  menuNavItems: MenuNavItems = MENU_NAV_ITEMS;
  userNavItems: MenuNavItems = USER_MENU_ITEMS;
  groupMenuItemSelected = signal<MenuItemGroupKey>(null);
  isRoutesChanging = signal<boolean>(false);

  // Observable that emits the filtered menu items for the selected group
  currentGroupRoutes$: Observable<MenuItem[]>;

  featuredRoutes = signal<MenuItem[]>([]);
  private unsubscribe$ = new Subject<void>();

  constructor(
    private store$: Store,
    private menuRoutesService: MenuRoutesService,
    private router: Router,
    private mobileService: MobileService,
    private timezoneService: TimezoneService
  ) {
    this.filteredMenuItems$ = this.observeUserRole();
    this.currentGroupRoutes$ = this.menuRoutesService.getRoutesForSelectedGroup$(this.filteredMenuItems$);

    // Set featured routes only if they're part of the current group after checking permissions
    this.currentGroupRoutes$.pipe(takeUntil(this.unsubscribe$)).subscribe(routes => {
      const potentialFeatured = [MENU_ITEMS_MAP.Projects, MENU_ITEMS_MAP.Schedule];
      const routeUrls = routes.map(r => r.url);
      const filtered = potentialFeatured.filter(item => routeUrls.includes(item.url));
      this.featuredRoutes.set(filtered);
    });
  }

  timezone = this.timezoneService.timezone;

  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

  itemClickHandler() {
    this.isMenuOpen.set(false);
    this.groupMenuItemSelected.set(null);
  }

  groupClickHandler(itemGroupKey?: MenuItemGroupKey) {
    if (itemGroupKey) {
      // If the same group is selected, close the menu
      if (this.groupMenuItemSelected() === itemGroupKey) {
        this.groupMenuItemSelected.set(null);
        this.menuRoutesService.clearSelectedGroup();
        this.isMenuOpen.set(false);
      } else {
        // If a different group is selected, animate the transition
        if (this.isMenuOpen()) {
          // Trigger animation for route change
          this.isRoutesChanging.set(true);

          // Wait for fade out, then change routes
          setTimeout(() => {
            this.groupMenuItemSelected.set(itemGroupKey);
            this.menuRoutesService.setSelectedGroup(itemGroupKey);
            this.isRoutesChanging.set(false);
          }, 150); // Half of the transition duration
        } else {
          // If menu is closed, just open with new group
          this.groupMenuItemSelected.set(itemGroupKey);
          this.menuRoutesService.setSelectedGroup(itemGroupKey);
          this.isMenuOpen.set(true);
        }
      }
    } else {
      this.groupMenuItemSelected.set(null);
      this.menuRoutesService.clearSelectedGroup();
      this.isMenuOpen.set(!this.isMenuOpen());
    }
  }

  userItemClickHandler(item: MenuNavItem) {
    this.isMenuOpen.set(false);
    this.groupMenuItemSelected.set(null);

    if (item?.meta?.isLogout) {
      this.logout.emit();
    }
    if ('url' in item && item.url) {
      this.router.navigate([item.url]);
    }
  }

  isNavItemVisible(item: MenuNavItem, filteredItems: MenuItem[]): boolean {
    const menuItem = item as MenuItem;
    if (!menuItem.rolePermissionKey) return true;
    return filteredItems.some(fi => fi.url === menuItem.url);
  }

  observeUserRole(): Observable<MenuItem[]> {
    return this.userRole$.pipe(
      filter(role => !!role),
      map(role => {
        const roleDisplayName = role.displayName?.toLowerCase();
        const rolePermissions = role.permissions;
        const isMobileApp = this.mobileService.isMobilePlatform();

        let menuItems = MENU_ITEMS;

        // Filter based on role
        if (roleDisplayName !== 'admin') {
          menuItems = menuItems.filter(item => !item.adminOnly);
          menuItems = getMenuItemsByRole(rolePermissions, menuItems);
        }

        // Filter based on mobile app context
        if (!isMobileApp) {
          menuItems = menuItems.filter(item => !item.mobileAppOnly);
        }

        return menuItems;
      }),
      takeUntil(this.unsubscribe$)
    );
  }
}
